import { useCallback, useState } from 'react'
import { CreditCardOutlined, PrinterOutlined } from '@ant-design/icons'
import { useInfiniteQuery, useMutation } from '@tanstack/react-query'
import { Alert, Button, Card, Flex, Skeleton, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { fetchHistoryPage, fetchStatement, type Statement } from '@/features/history/historyApi'
import { presetPeriod, type HistoryPeriod } from '@/features/history/historyPeriod'
import { HistoryPeriodPicker } from '@/features/history/HistoryPeriodPicker'
import { HistoryRows } from '@/features/history/HistoryRows'
import { StatementPrint } from '@/features/history/StatementPrint'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import { EmptyState } from '@/shared/ui/EmptyState'
import { LoadError } from '@/shared/ui/LoadError'

function BalanceFigure({ label, value, end = false }: { label: string; value: Money; end?: boolean }) {
  return (
    <Flex vertical align={end ? 'end' : 'start'}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Typography.Text>
      <Typography.Text strong style={{ fontSize: 18, whiteSpace: 'nowrap' }}>
        {formatMoneyValue(value)}
      </Typography.Text>
    </Flex>
  )
}

export function PaymentsPage() {
  const { t } = useTranslation()
  const [period, setPeriod] = useState<HistoryPeriod>(() => presetPeriod('days90'))
  const [statement, setStatement] = useState<Statement | null>(null)

  const history = useInfiniteQuery({
    queryKey: queryKeys.history(period.from, period.to),
    queryFn: ({ pageParam, signal }) => fetchHistoryPage(period, pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.hasNext ? last.page + 1 : undefined),
  })

  const print = useMutation({
    mutationFn: () => fetchStatement(period),
    onSuccess: setStatement,
  })

  const changePeriod = (next: HistoryPeriod) => {
    print.reset()
    setPeriod(next)
  }

  const finishPrint = useCallback(() => setStatement(null), [])

  const first = history.data?.pages[0]
  const rows = history.data?.pages.flatMap((page) => page.rows) ?? []

  return (
    <Flex vertical gap={16}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t('payments.title')}
        </Typography.Title>
        <Typography.Text type="secondary">{t('payments.subtitle')}</Typography.Text>
      </div>

      <Card>
        <Flex vertical gap={12}>
          <HistoryPeriodPicker period={period} onChange={changePeriod} />
          <Button
            icon={<PrinterOutlined />}
            loading={print.isPending}
            disabled={!history.isSuccess}
            onClick={() => print.mutate()}
            block
          >
            {t('history.print')}
          </Button>
        </Flex>
      </Card>

      {history.isPending ? (
        <Card>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      ) : null}

      {history.isError ? (
        <LoadError
          titleKey="payments.loadFailed"
          error={history.error}
          onRetry={() => void (history.isFetchNextPageError ? history.fetchNextPage() : history.refetch())}
        />
      ) : null}

      {print.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('history.printFailed')}
          description={translateError(print.error, t)}
        />
      ) : null}

      {first ? (
        <>
          <Card size="small">
            <Flex justify="space-between" gap={12} wrap>
              <BalanceFigure label={t('history.openingBalance')} value={first.opening} />
              <BalanceFigure label={t('history.closingBalance')} value={first.closing} end />
            </Flex>
          </Card>

          {rows.length === 0 ? (
            <EmptyState icon={<CreditCardOutlined />} title={t('history.empty')} />
          ) : (
            <Card styles={{ body: { padding: 0 } }}>
              <HistoryRows rows={rows} />
            </Card>
          )}

          {history.hasNextPage ? (
            <Button block loading={history.isFetchingNextPage} onClick={() => void history.fetchNextPage()}>
              {t('history.loadMore')}
            </Button>
          ) : null}
        </>
      ) : null}

      {statement ? <StatementPrint key={statement.generatedAt} statement={statement} onDone={finishPrint} /> : null}
    </Flex>
  )
}
